import { LoadingButton } from "@mui/lab";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { CampaignsContext } from "contexts/Campaigns";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { NewCampaignContext } from "contexts/NewCampaign";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Campaign, CampaignBrand, CampaignStatus } from "schemas/dashboard";

import { formatCampaignDate } from "utils/time";

import DeleteCampaignDialog from "../CampaignDetails/DeleteCampaignDialog";
import EditableCampaignDetails from "../CampaignDetails/EditableCampaignDetails";
import UneditableCampaignDetails from "../CampaignDetails/UneditableCampaignDetails";
import { CampaignStatusChip } from "../CampaignStatusChip";
import CampaignBrandCard from "../NewCampaign/BrandSearch/Brands/CampaignBrandCard";
import UncheckBrand from "../NewCampaign/BrandSearch/Brands/CampaignBrandCard/UncheckBrand";
import styles from "./styles";

export default function CampaignDialog() {
  const theme = useTheme();
  const { excludedBentoBrandIds } = useContext(NewCampaignContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const _isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileScreen =
    _isMobileScreen || (isMediumScreen && discoverTab !== null);
  const navigate = useNavigate();

  const { campaigns, getCampaign } = useContext(CampaignsContext);
  const { campaignId: id } = useParams();

  const campaignId = Number(id);

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignBrands, setCampaignBrands] = useState<CampaignBrand[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [loadingCampaign, setLoadingCampaign] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const partialCampaign = campaigns.find((c) => c.id === campaignId);

  const fetchCampaignBrands = async () => {
    if (page === 1) {
      setLoadingCampaign(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const res = await getCampaign(campaignId, page);
      if (!res) {
        return;
      }
      const { campaign, campaignBrands } = res;
      setCampaign(campaign);
      if (page === 1) {
        setCampaignBrands(campaignBrands);
      } else {
        setCampaignBrands((prev) => [...prev, ...campaignBrands]);
      }
      setPage(page + 1);
    } finally {
      setLoadingCampaign(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchCampaignBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const handleClose = () => {
    navigate("..");
  };

  const totalSelectedBrands =
    (partialCampaign?.campaignBrandCount || 0) -
    (excludedBentoBrandIds.size || 0);

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="lg"
      onClose={handleClose}
      fullScreen={isMobileScreen}
      PaperProps={{
        sx: { height: { md: "100%" } },
      }}
    >
      <IconButton
        sx={{
          color: "primary.main",
          fontSize: 14,
          position: "absolute",
          right: 10,
          top: 10,
        }}
        onClick={handleClose}
      >
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>

      <DialogTitle gap={2}>
        <Grid container alignItems="center" gap={1}>
          {campaign && formatCampaignDate(campaign.startDate, false)} to{" "}
          {campaign && formatCampaignDate(campaign.endDate, false)}
          {" | "}
          {partialCampaign?.name}
          {partialCampaign?.status && (
            <CampaignStatusChip
              status={partialCampaign?.status}
              startDate={partialCampaign?.startDate}
            />
          )}
        </Grid>
        {partialCampaign?.status === CampaignStatus.pending_approval && (
          <Box sx={styles.banner}>
            <strong>🪄 Bento has set up a campaign for you</strong>
            <br />
            All you have to do is review the brands and the templates selected,
            then hit schedule.
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container alignItems="center" rowGap={2}>
          <Grid
            item
            container
            justifyContent="space-between"
            xs={12}
            lg={8}
            gap={1}
          >
            {partialCampaign?.status === CampaignStatus.pending_approval ||
            partialCampaign?.status === CampaignStatus.expired ? (
              <>
                <Typography
                  variant="h6"
                  sx={{ fontSize: 15, color: grey[700] }}
                >
                  <strong>{totalSelectedBrands}</strong> Brands Are Selected For
                  This Campaign
                </Typography>
                <FormHelperText sx={{ mr: 3 }}>Selected</FormHelperText>
              </>
            ) : (
              <Typography variant="h6" sx={{ fontSize: 15, color: grey[700] }}>
                Campaign Brands ({partialCampaign?.campaignBrandCount})
              </Typography>
            )}
          </Grid>
          <Grid item container xs={12}>
            <Grid item container xs={12} lg={8} gap={1}>
              {campaignBrands.map((brand: CampaignBrand) => (
                <Grid item xs={12} key={brand.bentoBrand.id}>
                  <CampaignBrandCard
                    brand={brand.bentoBrand}
                    campaignBrand={brand}
                    status={partialCampaign?.status}
                    actions={
                      <>
                        {(partialCampaign?.status ===
                          CampaignStatus.pending_approval ||
                          partialCampaign?.status ===
                            CampaignStatus.expired) && (
                          <UncheckBrand
                            campaignBrand={brand}
                            totalBrands={
                              (partialCampaign?.campaignBrandCount || 0) -
                              (excludedBentoBrandIds.size || 0)
                            }
                            handleOpenDeleteDialog={() => {
                              setOpenDeleteDialog(true);
                            }}
                          />
                        )}
                      </>
                    }
                  />
                </Grid>
              ))}
              {partialCampaign?.campaignBrandCount &&
                !loadingCampaign &&
                campaignBrands.length < partialCampaign.campaignBrandCount && (
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    sx={{
                      color: grey[800],
                      backgroundColor: grey[200],
                    }}
                    color="inherit"
                    disableElevation
                    size="small"
                    onClick={() => {
                      fetchCampaignBrands();
                    }}
                    loading={loadingMore}
                  >
                    Load More
                  </LoadingButton>
                )}
            </Grid>
          </Grid>

          {partialCampaign?.status === CampaignStatus.pending_approval ||
          partialCampaign?.status === CampaignStatus.expired ? (
            <Box sx={{ ml: -2 }}>
              <EditableCampaignDetails
                campaign={campaign}
                totalSelectedBrands={totalSelectedBrands}
              />
            </Box>
          ) : (
            <UneditableCampaignDetails
              partialCampaign={partialCampaign}
              campaign={campaign}
            />
          )}
        </Grid>
      </DialogContent>

      {loadingCampaign && (
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: 9999,
          }}
          open={loadingCampaign}
        >
          <CircularProgress />
        </Backdrop>
      )}

      {campaign?.id && (
        <DeleteCampaignDialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          campaignId={campaign?.id}
        />
      )}
    </Dialog>
  );
}
